import { Application, Container, Renderer } from 'pixi.js';
import { handleGameSurfaceWheel } from '@core/UI/utils/autoplayModalScroll';

// Global PIXI variables for browser extensions and dev tools
declare global {
  var __PIXI_APP__: Application | undefined;
  var __PIXI_STAGE__: Container | undefined;
  var __PIXI_RENDERER__: Renderer | undefined;
}
export type PixiCanvasMountOptions = {
  resizeTo?: HTMLElement | Window | null;
};

export class PixiCanvas {
  app!: Application;
  root!: Container;
  async mount(el: HTMLElement, options: PixiCanvasMountOptions = {}) {
    this.app = new Application();
    const resolution = Math.min(window.devicePixelRatio || 1, 2);
    await this.app.init({
      resizeTo: options.resizeTo ?? window,
      autoDensity: true,
      resolution,
      antialias: true,
      background: '#0e0e0e', // PIXI 8.x uses background instead of backgroundColor
      powerPreference: 'low-power', // Prefer the lower-power GPU where available (mobile/Safari)
      preference: 'webgl',
      preserveDrawingBuffer: false,
      clearBeforeRender: true,
      eventMode: 'passive',
    });

    // Expose PIXI application globally for browser extensions and dev tools
    globalThis.__PIXI_APP__ = this.app;
    globalThis.__PIXI_STAGE__ = this.app.stage;
    globalThis.__PIXI_RENDERER__ = this.app.renderer;

    console.log(
      '[PixiCanvas] PIXI application exposed globally for browser extensions'
    );
    console.log('[PixiCanvas] Container size:', {
      width: this.app.screen.width,
      height: this.app.screen.height,
      canvasWidth: this.app.canvas.width,
      canvasHeight: this.app.canvas.height,
      resolution: this.app.renderer.resolution,
    });

    this.root = new Container();
    this.app.stage.addChild(this.root);
    const canvas = this.app.canvas as HTMLCanvasElement;
    canvas.style.display = 'block';
    el.appendChild(canvas);

    canvas.addEventListener('webglcontextlost', () => {
      console.error('WEBGL LOST');
    });
    canvas.addEventListener('webglcontextrestored', () => {
      console.error('WEBGL RESTORED');
    });

    // Forward wheel events from the game canvas area to the nearest scrollable
    // container so the page scrolls when the mouse is over the game screen.
    el.addEventListener(
      'wheel',
      (e: WheelEvent) => {
        if (e.ctrlKey) return; // Don't interfere with pinch-zoom prevention
        handleGameSurfaceWheel(e.deltaY);
      },
      { passive: true }
    );
  }
  unmount() {
    // Clean up global references
    if (globalThis.__PIXI_APP__ === this.app) {
      delete globalThis.__PIXI_APP__;
      delete globalThis.__PIXI_STAGE__;
      delete globalThis.__PIXI_RENDERER__;
    }

    this.app?.destroy(true, { children: true, texture: true });
  }
}
